import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import { getCaseStudies, CASE_STUDY_CATEGORIES, FEATURED_CASE_STUDY_ID } from '@/data/caseStudies';
import { useLiveTick } from '@/lib/liveContent';
import { Reveal } from '../Reveal';

export const FeaturedCaseStudy = () => {
  useLiveTick();
  const featured =
    getCaseStudies().find((cs) => cs.featured && cs.status === 'published') ||
    getCaseStudies().find((cs) => cs.id === FEATURED_CASE_STUDY_ID);
  if (!featured) return null;

  const primaryCategory = CASE_STUDY_CATEGORIES[featured.categories[0]];

  return (
    <section aria-labelledby="featured-heading" data-testid="featured-case-study" className="bg-mist py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-card">
            <div className="grid lg:grid-cols-2">
              <div className="relative">
                <img
                  src={featured.image.src}
                  alt={featured.image.alt}
                  width="1080"
                  height="720"
                  fetchPriority="high"
                  data-testid="featured-image"
                  className="h-full w-full object-cover"
                />
                <div className="absolute left-5 top-5 flex flex-wrap gap-2">
                  {featured.categories.map((key) => {
                    const cat = CASE_STUDY_CATEGORIES[key];
                    return (
                      <span
                        key={key}
                        className="rounded-full px-3.5 py-1.5 text-xs font-bold uppercase tracking-wide text-white"
                        style={{ backgroundColor: cat.color }}
                      >
                        {cat.label}
                      </span>
                    );
                  })}
                </div>
              </div>

              <div className="p-8 sm:p-10 lg:p-12">
                <p className="flex items-center gap-3">
                  <span className="font-mono text-xs font-medium tracking-widest" style={{ color: primaryCategory.color }}>01</span>
                  <span className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: primaryCategory.color }}>
                    Featured case study
                  </span>
                </p>
                <p className="mt-6 text-sm font-bold uppercase tracking-[0.16em] text-slate-500">{featured.client}</p>
                <h2 id="featured-heading" data-testid="featured-heading" className="mt-2 text-2xl font-extrabold tracking-tight text-navy-900 sm:text-3xl">
                  {featured.title}
                </h2>
                {featured.challenge && (
                  <p className="mt-5 text-sm leading-relaxed text-slate-600 sm:text-base">
                    <span className="font-bold text-navy-900">The challenge: </span>
                    {featured.challenge}
                  </p>
                )}
                {featured.solution && (
                  <p className="mt-4 text-sm leading-relaxed text-slate-600 sm:text-base">
                    <span className="font-bold text-navy-900">Amden’s involvement: </span>
                    {featured.solution}
                  </p>
                )}
                {featured.outcomes.length > 0 && (
                  <ul className="mt-6 space-y-3">
                    {featured.outcomes.slice(0, 3).map((outcome) => (
                      <li key={outcome} className="flex items-start gap-2.5 text-sm font-semibold text-navy-800 sm:text-base">
                        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" style={{ color: primaryCategory.color }} aria-hidden="true" />
                        {outcome}
                      </li>
                    ))}
                  </ul>
                )}
                {featured.services.length > 0 && (
                  <div className="mt-6 flex flex-wrap gap-2">
                    {featured.services.map((service) => (
                      <span
                        key={service}
                        className="rounded-full px-3.5 py-1.5 text-xs font-bold text-navy-800"
                        style={{ backgroundColor: primaryCategory.soft }}
                      >
                        {service}
                      </span>
                    ))}
                  </div>
                )}
                <Link
                  to={`/case-studies/${featured.slug}`}
                  data-testid="featured-read-more"
                  className="group mt-8 inline-flex items-center gap-2 rounded-md bg-navy-900 px-6 py-3.5 text-sm font-bold text-white transition-colors hover:bg-navy-700"
                >
                  Read the Full Case Study
                  <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" aria-hidden="true" />
                </Link>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
